import React, { useEffect, useRef, useState } from 'react';
import { getShipmentMapConfig } from '../config/api';

let googleMapsLoader;
const geocodeCache = new Map();
const MAX_GEOCODE_CACHE_SIZE = 250;

const loadGoogleMaps = (apiKey) => {
  if (window.google?.maps) return Promise.resolve(window.google.maps);
  if (googleMapsLoader) return googleMapsLoader;

  googleMapsLoader = new Promise((resolve, reject) => {
    const callbackName = '__shipmentGoogleMapsReady';
    const script = document.createElement('script');
    let settled = false;

    const fail = (message) => {
      if (settled) return;
      settled = true;
      googleMapsLoader = null;
      reject(new Error(message));
    };

    window[callbackName] = () => {
      if (settled) return;
      settled = true;
      delete window[callbackName];
      resolve(window.google.maps);
    };
    window.gm_authFailure = () => fail('Google Maps rejected the browser API key. Check Maps JavaScript API and referrer restrictions.');

    script.id = 'shipment-google-maps-script';
    script.async = true;
    script.defer = true;
    script.onerror = () => fail('Google Maps could not be loaded.');
    script.src = `https://maps.googleapis.com/maps/api/js?key=${encodeURIComponent(apiKey)}&libraries=geometry&v=weekly&callback=${callbackName}`;
    document.head.appendChild(script);
  });

  return googleMapsLoader;
};

const geocode = (geocoder, address) => {
  const normalizedAddress = String(address || '').trim().toLowerCase().replace(/\s+/g, ' ');
  if (geocodeCache.has(normalizedAddress)) return geocodeCache.get(normalizedAddress);
  const request = new Promise((resolve, reject) => {
    geocoder.geocode({ address }, (results, status) => {
      if (status === 'OK' && results?.[0]) resolve(results[0].geometry.location.toJSON());
      else reject(new Error(`Location could not be found for ${address}`));
    });
  }).catch(error => {
    geocodeCache.delete(normalizedAddress);
    throw error;
  });
  geocodeCache.set(normalizedAddress, request);
  if (geocodeCache.size > MAX_GEOCODE_CACHE_SIZE) geocodeCache.delete(geocodeCache.keys().next().value);
  return request;
};

const mapWithConcurrency = async (items, concurrency, mapper) => {
  const results = new Array(items.length);
  let nextIndex = 0;
  const worker = async () => {
    while (nextIndex < items.length) {
      const index = nextIndex;
      nextIndex += 1;
      results[index] = await mapper(items[index], index);
    }
  };
  await Promise.all(Array.from({ length: Math.min(concurrency, items.length) }, worker));
  return results;
};

const confirmedPosition = (stop) => {
  const latitude = Number(stop.location?.latitude);
  const longitude = Number(stop.location?.longitude);
  return Number.isFinite(latitude) && Number.isFinite(longitude) ? { lat: latitude, lng: longitude } : null;
};

const resolveStopPosition = async (geocoder, stop) => {
  const savedPosition = confirmedPosition(stop);
  if (savedPosition) return { position: savedPosition, source: 'confirmed' };
  const pincodeAddress = [stop.area, stop.pincode, 'India'].filter(Boolean).join(', ');
  const hasAddress = stop.locationSource !== 'pincode' && String(stop.address || '').trim();
  if (hasAddress) {
    try {
      return { position: await geocode(geocoder, stop.address), source: 'address' };
    } catch (_) {
      // A pincode marker is still useful when Google cannot resolve the full address.
    }
  }
  return { position: await geocode(geocoder, pincodeAddress), source: 'pincode' };
};

const markerLabel = (text, backgroundColor) => ({
  text,
  color: '#ffffff',
  fontSize: '11px',
  fontWeight: '700',
  className: `shipment-google-marker shipment-google-marker-${backgroundColor}`,
});

const checkpointIcon = (maps, status, source) => ({
  path: maps.SymbolPath.CIRCLE,
  scale: status === 'next' ? 16 : 14,
  fillColor: source === 'confirmed' ? '#198754' : source === 'pincode' ? '#f59f00' : '#0d6efd',
  fillOpacity: 1,
  strokeColor: '#ffffff',
  strokeWeight: 3,
});

const createCheckpointInfo = (stop, status, source) => {
  const container = document.createElement('div');
  container.className = 'shipment-map-checkpoint-info';

  const heading = document.createElement('div');
  heading.className = 'shipment-map-checkpoint-info-heading';

  const title = document.createElement('strong');
  title.textContent = `Checkpoint ${stop.sequence}`;
  heading.appendChild(title);

  const statusLabel = document.createElement('span');
  statusLabel.className = `is-${status}`;
  statusLabel.textContent = status === 'completed' ? 'Completed' : status === 'next' ? 'Next stop' : 'Upcoming';
  heading.appendChild(statusLabel);
  container.appendChild(heading);

  const location = document.createElement('div');
  location.className = 'shipment-map-checkpoint-info-location';
  location.textContent = [stop.area, stop.pincode].filter(Boolean).join(' · ');
  container.appendChild(location);

  const locationSource = document.createElement('div');
  locationSource.className = `shipment-map-checkpoint-source is-${source}`;
  locationSource.textContent = source === 'confirmed'
    ? 'Confirmed from a previous delivery pin'
    : source === 'address' ? 'Located from the full delivery address' : 'Approximate pincode area';
  container.appendChild(locationSource);

  const address = document.createElement('p');
  address.textContent = stop.address || [stop.area, stop.pincode, 'India'].filter(Boolean).join(', ');
  container.appendChild(address);

  const orderIds = Array.isArray(stop.orderIds) ? stop.orderIds : [];
  const orders = document.createElement('div');
  orders.className = 'shipment-map-checkpoint-info-orders';
  const deliveredCount = Array.isArray(stop.deliveredOrderIds) ? stop.deliveredOrderIds.length : 0;
  orders.textContent = `${orderIds.length || stop.orders || 0} order${(orderIds.length || stop.orders) === 1 ? '' : 's'} · ${deliveredCount} delivered`;
  container.appendChild(orders);

  if (orderIds.length) {
    const orderList = document.createElement('small');
    orderList.textContent = orderIds.join(', ');
    container.appendChild(orderList);
  }

  return container;
};

const GoogleShipmentMap = ({ shipment, currentLocation = null, pendingPin = null, interactive = false, onPinChange = null, onStopSelect = null, focusedStop = null }) => {
  const mapElement = useRef(null);
  const mapRef = useRef(null);
  const mapsRef = useRef(null);
  const currentMarkerRef = useRef(null);
  const pendingMarkerRef = useRef(null);
  const interactiveRef = useRef(interactive);
  const onPinChangeRef = useRef(onPinChange);
  const onStopSelectRef = useRef(onStopSelect);
  const stopMarkersRef = useRef(new Map()); // sequence -> marker, for card↔map sync
  const [state, setState] = useState({ loading: true, error: '' });

  useEffect(() => { interactiveRef.current = interactive; }, [interactive]);
  useEffect(() => { onPinChangeRef.current = onPinChange; }, [onPinChange]);
  useEffect(() => { onStopSelectRef.current = onStopSelect; }, [onStopSelect]);

  useEffect(() => {
    let cancelled = false;
    const mapObjects = [];

    const renderMap = async () => {
      setState({ loading: true, error: '' });
      try {
        const configResponse = await getShipmentMapConfig();
        const apiKey = configResponse.data?.apiKey;
        if (!apiKey) throw new Error('Google Maps browser key is not configured.');
        const maps = await loadGoogleMaps(apiKey);
        if (cancelled || !mapElement.current) return;

        const map = new maps.Map(mapElement.current, {
          center: { lat: 22.9734, lng: 78.6569 },
          zoom: 5,
          mapTypeControl: false,
          fullscreenControl: true,
          streetViewControl: false,
          gestureHandling: 'cooperative',
        });
        mapRef.current = map;
        mapsRef.current = maps;
        const bounds = new maps.LatLngBounds();
        const encodedPolyline = typeof shipment.encodedPolyline === 'string' ? shipment.encodedPolyline : '';
        const routePath = encodedPolyline && maps.geometry?.encoding
          ? maps.geometry.encoding.decodePath(encodedPolyline)
          : [];

        if (routePath.length) {
          const routeLine = new maps.Polyline({
            map,
            path: routePath,
            strokeColor: '#0d6efd',
            strokeOpacity: 0.9,
            strokeWeight: 5,
          });
          mapObjects.push(routeLine);
          routePath.forEach(point => bounds.extend(point));

        }

        const hubPosition = shipment.hubLocation?.latitude && shipment.hubLocation?.longitude
          ? { lat: Number(shipment.hubLocation.latitude), lng: Number(shipment.hubLocation.longitude) }
          : routePath[0] || null;
        if (hubPosition) {
          bounds.extend(hubPosition);
          mapObjects.push(new maps.Marker({ map, position: hubPosition, label: markerLabel('H', 'hub'), title: `${shipment.hub} · route starts and returns here` }));
        }

        const geocoder = new maps.Geocoder();
        const stops = shipment.stops || [];
        const nextStopSequence = shipment.nextStop?.sequence
          ?? stops.find(stop => !stop.completed)?.sequence;
        const checkpointInfoWindow = new maps.InfoWindow();
        mapObjects.push({ remove: () => checkpointInfoWindow.close() });
        const resolvedStops = await mapWithConcurrency(stops, 4, async stop => {
          try {
            const resolved = await resolveStopPosition(geocoder, stop);
            return { stop, ...resolved };
          } catch (_) {
            return { stop, position: null, source: stop.locationSource || 'pincode' };
          }
        });
        if (cancelled) return;
        for (const resolved of resolvedStops) {
          const { stop, position, source } = resolved;
          if (position) {
            bounds.extend(position);
            const status = stop.completed
              ? 'completed'
              : String(stop.sequence) === String(nextStopSequence) ? 'next' : 'pending';
            const marker = new maps.Marker({
              map,
              position,
              icon: checkpointIcon(maps, status, source),
              label: markerLabel(String(stop.sequence), status),
              title: `${stop.area} · ${stop.pincode} · ${stop.deliveredOrderIds?.length || 0}/${stop.orders} delivered · ${source === 'confirmed' ? 'confirmed pin' : source === 'address' ? 'full address' : 'approximate pincode'}`,
              zIndex: status === 'next' ? 4 : status === 'completed' ? 2 : 3,
            });
            const markerClickListener = marker.addListener('click', () => {
              checkpointInfoWindow.setContent(createCheckpointInfo(stop, status, source));
              checkpointInfoWindow.open({ map, anchor: marker });
              onStopSelectRef.current?.(stop.sequence);
            });
            stopMarkersRef.current.set(String(stop.sequence), marker);
            mapObjects.push(marker, markerClickListener);
          }
        }

        (shipment.deliveryStops || []).forEach(stop => {
          const position = { lat: Number(stop.latitude), lng: Number(stop.longitude) };
          bounds.extend(position);
          mapObjects.push(new maps.Marker({ map, position, label: markerLabel('✓', 'delivery'), title: `Delivered: ${(stop.orderIds || []).join(', ')}` }));
        });

        const mapClickListener = map.addListener('click', event => {
          if (!interactiveRef.current || !onPinChangeRef.current) return;
          onPinChangeRef.current({ latitude: event.latLng.lat(), longitude: event.latLng.lng(), accuracyMeters: null });
        });
        mapObjects.push(mapClickListener);

        if (bounds.isEmpty()) throw new Error(shipment.routeError || 'No route locations are available for this shipment.');
        map.fitBounds(bounds, 38);
        maps.event.addListenerOnce(map, 'idle', () => {
          if (map.getZoom() > 15) map.setZoom(15);
        });
        if (!cancelled) setState({ loading: false, error: '' });
      } catch (error) {
        if (!cancelled) setState({ loading: false, error: error.message || 'Google Maps could not be displayed.' });
      }
    };

    renderMap();
    const stopMarkers = stopMarkersRef.current;
    return () => {
      cancelled = true;
      mapObjects.forEach(object => object.remove ? object.remove() : object.setMap?.(null));
      currentMarkerRef.current?.setMap(null);
      pendingMarkerRef.current?.setMap(null);
      currentMarkerRef.current = null;
      pendingMarkerRef.current = null;
      stopMarkers.clear();
      mapRef.current = null;
      mapsRef.current = null;
    };
  }, [shipment]);

  // Card → map: when a stop is focused from the order list, pan to its marker
  // and bounce it briefly so the driver sees which stop the card refers to.
  useEffect(() => {
    const maps = mapsRef.current;
    const map = mapRef.current;
    if (!maps || !map || focusedStop == null || state.loading) return undefined;
    const marker = stopMarkersRef.current.get(String(focusedStop));
    if (!marker) return undefined;
    map.panTo(marker.getPosition());
    marker.setAnimation(maps.Animation.BOUNCE);
    const timer = window.setTimeout(() => marker.setAnimation(null), 1400);
    return () => window.clearTimeout(timer);
  }, [focusedStop, state.loading]);

  useEffect(() => {
    const maps = mapsRef.current;
    const map = mapRef.current;
    if (!maps || !map || !currentLocation) return;
    const position = { lat: Number(currentLocation.latitude), lng: Number(currentLocation.longitude) };
    if (!currentMarkerRef.current) currentMarkerRef.current = new maps.Marker({ map, position, title: 'Current driver location', icon: { path: maps.SymbolPath.CIRCLE, scale: 8, fillColor: '#0d6efd', fillOpacity: 1, strokeColor: '#fff', strokeWeight: 3 } });
    else currentMarkerRef.current.setPosition(position);
    return undefined;
  }, [currentLocation, state.loading]);

  useEffect(() => {
    const maps = mapsRef.current;
    const map = mapRef.current;
    if (!maps || !map) return;
    if (!pendingPin) {
      pendingMarkerRef.current?.setMap(null);
      pendingMarkerRef.current = null;
      return;
    }
    const position = { lat: Number(pendingPin.latitude), lng: Number(pendingPin.longitude) };
    if (!pendingMarkerRef.current) pendingMarkerRef.current = new maps.Marker({ map, position, title: 'Selected delivery pin', draggable: true, animation: maps.Animation.DROP });
    else pendingMarkerRef.current.setPosition(position);
    const listener = pendingMarkerRef.current.addListener('dragend', event => onPinChangeRef.current?.({ latitude: event.latLng.lat(), longitude: event.latLng.lng(), accuracyMeters: null }));
    return () => listener.remove();
  }, [pendingPin, state.loading]);

  return (
    <div className="shipment-google-map-shell">
      <div ref={mapElement} className="shipment-google-map-canvas" aria-label="Shipment delivery route map" />
      {state.loading && <div className="shipment-google-map-overlay">Loading Google Map…</div>}
      {state.error && <div className="shipment-google-map-overlay shipment-google-map-error">{state.error}</div>}
      {!state.loading && !state.error && (
        <div className="shipment-map-checkpoint-legend" aria-label="Map checkpoint legend">
          <span><i className="is-hub">H</i>Hub</span>
          <span><i className="is-confirmed">✓</i>Confirmed pin</span>
          <span><i className="is-address" />Full address</span>
          <span><i className="is-pincode" />Pincode area</span>
        </div>
      )}
      {!state.loading && !state.error && shipment.routeStatus !== 'ready' && <div className="shipment-google-map-fallback">Showing available pincode locations · road route unavailable</div>}
      {!state.loading && !state.error && interactive && <div className="shipment-google-map-pin-hint">Tap the map to place the delivery pin</div>}
    </div>
  );
};

export default GoogleShipmentMap;
